import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Container,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

import ActionHeaderCard from "../components/General/ActionHeaderCard";
import DatagridCustom from "../components/General/DatagridCustom";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import ClientFormDialog from "../components/Client/ClientFormDialog";
import ClientDetailDialog from "../components/Client/ClientDetailDialog";

import { getClients, deleteClient } from "../services/ClientService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

export default function Client() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });

  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailClientId, setDetailClientId] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const canCreate = hasPermission("crear cliente");
  const canUpdate = hasPermission("actualizar cliente");
  const canDelete = hasPermission("eliminar cliente");
  const canViewDetail = hasPermission("ver detalle cliente");

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const searchTerm = filterModel.quickFilterValues?.join(" ") || "";

      const res = await getClients({ page: page + 1, perPage: pageSize, search: searchTerm });
      if (res.data?.status === "success") {
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      showToast("No se pudo cargar el listado de clientes.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleFilterModelChange = useCallback(
    (newFilterModel) => {
      const currentSearchTerm = filterModel.quickFilterValues?.join(" ") || "";
      const newSearchTerm = newFilterModel.quickFilterValues?.join(" ") || "";

      setFilterModel(newFilterModel);

      if (currentSearchTerm !== newSearchTerm) {
        setPaginationModel((prev) => (prev.page !== 0 ? { ...prev, page: 0 } : prev));
      }
    },
    [filterModel],
  );

  const openCreate = () => {
    setEditData(null);
    setFormOpen(true);
  };

  const openEdit = (client) => {
    setEditData(client);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditData(null);
  };

  const handleSaved = () => {
    closeForm();
    fetchClients();
  };

  const openDetail = (client) => {
    setDetailClientId(client.id);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailClientId(null);
  };

  const handleOpenDeleteConfirm = (id) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      const res = await deleteClient(deleteTargetId);
      showToast(res.data?.message || "Cliente eliminado.", "success");
      fetchClients();
    } catch (err) {
      showToast(
        err.response?.data?.message || "No se pudo eliminar el cliente.",
        "error",
      );
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "name", headerName: "Nombre / Razón social", flex: 1.5 },
    { field: "document", headerName: "Documento", flex: 1 },
    { field: "parties_count", headerName: "Partes", flex: 0.6 },
    { field: "legal_files_count", headerName: "Expedientes", flex: 0.7 },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <>
          <Tooltip title="Ver partes y expedientes">
            <span>
              <IconButton
                color="primary"
                onClick={() => openDetail(params.row)}
                disabled={!canViewDetail}
              >
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Editar cliente">
            <span>
              <IconButton
                color="secondary"
                onClick={() => openEdit(params.row)}
                disabled={!canUpdate}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Eliminar cliente">
            <span>
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteConfirm(params.row.id)}
                disabled={!canDelete}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<BadgeRoundedIcon />}
            title="Clientes"
            description="Agrupá las partes que corresponden a la misma persona o empresa, aunque figuren con nombres distintos en los expedientes."
            action={
              canCreate ? (
                <Button
                  onClick={openCreate}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
                >
                  Nuevo cliente
                </Button>
              ) : null
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <DatagridCustom
            rows={rows}
            columns={columns}
            title="Listado de Clientes"
            description="Buscá por nombre o documento."
            icon={<BadgeRoundedIcon sx={{ fontSize: 32 }} />}
            getRowId={(row) => row.id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            total={total}
            filterModel={filterModel}
            onFilterChange={handleFilterModelChange}
            filterMode="server"
            isServerPagination
          />
        </Grid>
      </Grid>

      <ClientFormDialog
        open={formOpen}
        onClose={closeForm}
        editData={editData}
        onSaved={handleSaved}
      />

      <ClientDetailDialog
        open={detailOpen}
        onClose={closeDetail}
        clientId={detailClientId}
        onChanged={fetchClients}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar cliente"
        description="¿Eliminar este cliente? Las partes vinculadas quedarán sin cliente asignado, no se eliminan. Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />
    </Container>
  );
}
